import type { Task } from '@/types/task';

/**
 * Empty-state copy for a thread opened from a task ("Open in chat" on the
 * task panel). Nothing is sent on arrival — the thread opens on the task's own
 * greeting with the task in the panel beside it — so this is what the user
 * lands on: what the thread is for, and chips that prefill a first message.
 */
export const TASK_CHAT_EMPTY_STATE_SUBTITLE =
  'Ask a question or make a change to this task.';

/**
 * Starter prompts every task offers. Prefill only: the user still sends. Kept
 * to what applies to any task — a task's own work (validate, reconcile, …) is
 * for the user to ask for.
 */
export const TASK_CHAT_STARTER_PROMPTS: string[] = [
  'Summarise the task',
  'Mark task as done',
];

/**
 * The task as the agent receives it, sent as chat context before the first
 * message. The id travels here rather than only in the user's sentence,
 * because `task_update` takes the id and would otherwise have to go looking
 * for a task the reader is already pointing at.
 */
export function buildTaskChatContext(task: Pick<Task, 'id' | 'key' | 'title'>): string {
  return JSON.stringify({
    type: 'task',
    task: { id: task.id, key: task.key, title: task.title },
  });
}

/**
 * Mirrors `TASK_TITLE_MAX_LENGTH` / `TASK_DESCRIPTION_MAX_LENGTH` in
 * `apps/server/src/task/constant.ts`. The panel refuses anything longer in
 * place, so an over-long paste is flagged on the field rather than sent and
 * turned away as a failed save.
 */
export const TASK_TITLE_MAX_LENGTH = 200;

export const TASK_DESCRIPTION_MAX_LENGTH = 10000;
